// @ts-ignore

import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowLeft,
  faArrowRight,
  faArrowRightFromBracket,
  faArrowUpRightFromSquare,
  faBell,
  faBellSlash,
  faBolt,
  faBook,
  faBuilding,
  faCheck,
  faChevronDown,
  faChevronRight,
  faChevronUp,
  faCircle,
  faClipboardCheck,
  faClock,
  faCloudShowersHeavy,
  faCog,
  faCommentAlt,
  faCrown,
  faDownload,
  faEdit,
  faExclamationTriangle,
  faEye,
  faFile,
  faFileContract,
  faGift,
  faHeartbeat,
  faInfoCircle,
  faKey,
  faLifeRing,
  faLink,
  faListOl,
  faLock,
  faLockOpen,
  faMagnifyingGlass,
  faPen,
  faPlus,
  faQuestionCircle,
  faRobot,
  faRocket,
  faSave,
  faSignOutAlt,
  faSimCard,
  faSolarPanel,
  faSpinner,
  faSyncAlt,
  faTachometerAlt,
  faTags,
  faTicketAlt,
  faTimes,
  faTrash,
  faUmbrellaBeach,
  faUser,
  faUsers,
  faWifi,
} from '@fortawesome/free-solid-svg-icons';
import {
  faBoltSlash,
  faChevronsDown,
  faChevronsLeft,
  faChevronsRight,
  faChevronsUp,
  faCompass,
  faDownToLine,
  faGaugeLow,
  faGaugeMed,
  faLightbulb,
  faMeterBolt,
  faSensorTriangleExclamation,
  faTransformerBolt,
  faUpFromLine,
  faWavePulse,
} from '@fortawesome/pro-solid-svg-icons';
import {
  FontAwesomeIcon,
  FontAwesomeLayers,
  FontAwesomeLayersText,
} from '@fortawesome/vue-fontawesome'; // Google related imports.
import { createIntercom } from '@mathieustan/vue-intercom'; // Google Maps related imports.
import * as Sentry from '@sentry/vue';
import { createApp } from 'vue';
import { GoogleMap } from 'vue3-google-map';
import Api from '@/data/Api';
import { i18n } from '@/i18n';
import App from '@/presentation/App.vue';
import HelpIcon from '@/presentation/components/HelpIcon.vue';
import { vSafeHtml } from '@/presentation/directives/vSafeHtml.ts';
import router from '@/presentation/router/router';
import '@/presentation/assets/scss/base.scss';
import 'primeicons/primeicons.css';

import { faLightbulbOn } from '@fortawesome/pro-solid-svg-icons/faLightbulbOn';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import { createPinia } from 'pinia';
import { ConfirmationService } from 'primevue';
import PrimeVue from 'primevue/config';
import VueGtag from 'vue-gtag';
import { initPostHog } from '@/infrastructure/analytics/PostHog.ts';

library.add(
  faArrowLeft,
  faArrowUpRightFromSquare,
  faArrowRight,
  faArrowRightFromBracket,
  faBell,
  faBellSlash,
  faBolt,
  faBoltSlash,
  faBook,
  faBuilding,
  faCheck,
  faChevronsLeft,
  faChevronsDown,
  faChevronsUp,
  faChevronsRight,
  faChevronDown,
  faChevronRight,
  faChevronUp,
  faCircle,
  faClipboardCheck,
  faClock,
  faCloudShowersHeavy,
  faCog,
  faCommentAlt,
  faCompass,
  faCrown,
  faDownload,
  faDownToLine,
  faEdit,
  faExclamationTriangle,
  faEye,
  faFile,
  faFileContract,
  faGift,
  faGaugeLow,
  faGaugeMed,
  faHeartbeat,
  faInfoCircle,
  faKey,
  faLifeRing,
  faLightbulb,
  faLightbulbOn,
  faLink,
  faListOl,
  faLock,
  faLockOpen,
  faMagnifyingGlass,
  faMeterBolt,
  faPen,
  faPlus,
  faQuestionCircle,
  faRobot,
  faRocket,
  faSave,
  faSensorTriangleExclamation,
  faSignOutAlt,
  faSimCard,
  faSolarPanel,
  faSpinner,
  faSyncAlt,
  faTachometerAlt,
  faTags,
  faTicketAlt,
  faTimes,
  faTrash,
  faTransformerBolt,
  faUmbrellaBeach,
  faUser,
  faUsers,
  faUpFromLine,
  faWavePulse,
  faWifi,
);

localStorage.removeItem('app');

const app = createApp({
  extends: App,
});

const AmberPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{amber.50}',
      100: '{amber.100}',
      200: '{amber.200}',
      300: '{amber.300}',
      400: '{amber.400}',
      500: '{amber.500}',
      600: '{amber.600}',
      700: '{amber.700}',
      800: '{amber.800}',
      900: '{amber.900}',
      950: '{amber.950}',
    },
  },
});

app.use(PrimeVue, {
  theme: {
    preset: AmberPreset,
    options: {
      prefix: 'p',
      darkModeSelector: 'p',
      cssLayer: {
        name: 'primevue',
        order: 'tailwind-base, primevue, tailwind-utilities',
      },
    },
  },
});

app.use(ConfirmationService);

// Register help icon
app.component('HelpIcon', HelpIcon);

app.component('FontAwesomeIcon', FontAwesomeIcon);
app.component('FontAwesomeLayers', FontAwesomeLayers);
app.component('FontAwesomeLayersText', FontAwesomeLayersText);

// Sanitizing replacement for v-html (prevents stored XSS). Usage: v-safe-html
app.directive('safe-html', vSafeHtml);

// @ts-ignore
app.use(GoogleMap);

// Include intercom
if (import.meta.env.VITE_INTERCOM_APP_ID) {
  const intercom = createIntercom({
    appId: import.meta.env.VITE_INTERCOM_APP_ID,
  });
  app.use(intercom);
}

// Sentry
Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [],
  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets: ['localhost', 'https://app.sundata.nl'],
  ignoreErrors: ['The provided access token has expired.'],
});

// Use google analytics for data logging. Set your google analytics ID in a environment variable
// noinspection JSUnresolvedReference
if (
  import.meta.env.VITE_NODE_ENV !== 'development' &&
  import.meta.env.VITE_GOOGLE_ANALYTICS_ID
) {
  app.use(
    VueGtag,
    {
      config: { id: import.meta.env.VITE_GOOGLE_ANALYTICS_ID },
    },
    router,
  );
}

initPostHog();

const pinia = createPinia();

app.use(i18n);

// Dynamically set the website title
router.beforeEach((to: any, from: any, next: any): void => {
  const metaTitle = to.meta.title;
  const subTitle = metaTitle
    ? i18n.global.t(metaTitle[0], metaTitle[1])
    : to.name;
  document.title = 'SunData | ' + subTitle;

  next();
});
// avoid circular dependency/import
Api.router = router;

app.use(pinia).use(router).mount('#app');
